/**
 * @module utils/splitReveal
 *
 * Per-line/word SplitText entrance reveal, triggered once when the element
 * scrolls into view. The element is unhidden as part of the reveal — it starts
 * at opacity 0 in CSS (`[data-inview='split']`) so there's no flash.
 *
 * Variants (opts.variant):
 *   wipe  — lines translate up out of a clipping mask (default; standalone text).
 *   scale — words scale up from 0 anchored bottom-centre, slightly staggered
 *           (used by FitHeading headings).
 *
 * Ownership modes:
 *   autoSplit: true  — SplitText re-splits itself on line-count changes (resize
 *     / late font swap). Use for standalone text with no other owner.
 *   autoSplit: false — a single split the caller manages. Use when something
 *     else (FitHeading) re-sizes the text and must revert → re-measure →
 *     re-split, so SplitText must not also re-split underneath it.
 *
 * The split is temporary either way: as soon as the reveal finishes the wrappers
 * are reverted and the authored markup is back (see restoreMarkup below), so
 * the text reflows, selects and reads normally for the rest of the page view.
 * That means autoSplit's re-splitting only covers the window before the reveal
 * plays, and `animate: false` (a re-split after it has played) does nothing at
 * all beyond making sure the text is visible.
 */

import { loadSplitText, refreshScroll } from '@/utils/gsap';

const VARIANTS = {
	// `linesClass` lets `.c-split-line-mask` (animations.css) extend the clip
	// region (overflow-clip-margin) so caps/descenders aren't trimmed. yPercent
	// must clear that clip margin or the hidden line peeks through.
	wipe: {
		type: 'lines',
		unit: 'lines',
		mask: true,
		lineClass: 'c-split-line',
		from: { yPercent: 130 },
		to: { yPercent: 0, duration: 0.9, ease: 'power4.out' },
		stagger: 0,
	},
	// Split words (within lines so wrapping is correct) and scale each from 0,
	// anchored to its bottom centre, with a small stagger so the line grows up.
	scale: {
		type: 'lines',
		unit: 'lines',
		mask: true,
		lineClass: 'c-split-line-scale',
		from: { yPercent: 130, scale: 0, transformOrigin: '50% 100%' },
		to: {
			yPercent: 0,
			scale: 1,
			transformOrigin: '50% 100%',
			duration: 0.8,
			ease: 'ease-this', // matches the --ease-this CSS token; snappy, no overshoot
		},
		stagger: 0.2,
	},
};

const BLOCK_TAG = /^(P|DIV|UL|OL|LI|BLOCKQUOTE|H[1-6]|FIGURE|SECTION|ARTICLE)$/;

// Put the authored markup back once the reveal has played: the line/mask
// wrappers have no job after that, and leaving them in place keeps SplitText's
// wrapping frozen against the layout it was measured in (and hands screen
// readers and text selection a stack of divs instead of a paragraph).
//
// Deferred a frame because this runs from the reveal's own onComplete — GSAP is
// still inside that animation, and reverting pulls its targets out of the DOM.
//
// Revert only restores innerHTML and the aria attributes, so the `opacity: 1`
// this module sets on the element itself survives — the text stays shown, it
// just goes back to being plain text.
function restoreMarkup(instance) {
	requestAnimationFrame(() => {
		instance.revert();
		refreshScroll();
	});
}

// Split each block-level child separately (rich text: multiple <p>, lists) so
// their block structure and inter-paragraph spacing survive — splitting the
// whole container flattens the paragraphs into one stream of lines. A leaf
// element (a heading, a single paragraph) splits itself.
function resolveTargets(el) {
	const blocks = [...el.children].filter(child =>
		BLOCK_TAG.test(child.tagName)
	);
	return blocks.length ? blocks : el;
}

export default async function splitReveal(
	el,
	{
		start = 'top 70%',
		autoSplit = false,
		animate = true,
		variant = 'wipe',
		delay = 0,
		onReveal,
		onRevealLead = 0,
	} = {}
) {
	const { gsap, SplitText } = await loadSplitText();
	const targets = resolveTargets(el);
	const v = VARIANTS[variant] ?? VARIANTS.wipe;

	const splitVars = {
		type: v.type,
		linesClass: v.lineClass,
		...(v.mask ? { mask: 'lines' } : {}),
	};

	const tweenTo = {
		...v.to,
		delay,
		stagger: v.stagger,
		scrollTrigger: { trigger: el, start, once: true },
	};

	if (autoSplit) {
		// Re-splits (resize / late font swap) only matter BEFORE the reveal has
		// played — afterwards the split is reverted, which also ends autoSplit's
		// own resize handling, so onSplit runs the entrance every time it fires.
		const instance = SplitText.create(targets, {
			...splitVars,
			autoSplit: true,
			onSplit(self) {
				gsap.set(el, { opacity: 1 });
				// Built as a timeline (rather than a bare tween) so onReveal can
				// fire `onRevealLead` seconds BEFORE the reveal finishes — letting
				// a follow-on (e.g. a deferred button) overlap the tail end.
				// Returning it lets autoSplit revert it cleanly before re-splitting.
				const tl = gsap.timeline({
					delay,
					scrollTrigger: { trigger: el, start, once: true },
					onComplete: () => restoreMarkup(self),
				});
				tl.fromTo(self[v.unit], v.from, { ...v.to, stagger: v.stagger });
				if (onReveal) {
					tl.call(onReveal, null, Math.max(0, tl.duration() - onRevealLead));
				}
				return tl;
			},
		});
		refreshScroll();
		return { revert: () => instance.revert() };
	}

	// Re-split after the reveal already played (the owner re-fitting on resize):
	// there's nothing left to animate out of, so skip the split entirely and
	// leave the authored markup alone — just make sure the text is shown.
	if (!animate) {
		gsap.set(el, { opacity: 1 });
		refreshScroll();
		return { revert: () => {} };
	}

	// Manual single split — the owner re-splits on resize.
	const instance = new SplitText(targets, splitVars);
	gsap.set(el, { opacity: 1 });

	const tween = gsap.fromTo(instance[v.unit], v.from, {
		...tweenTo,
		onComplete: () => {
			onReveal?.();
			restoreMarkup(instance);
		},
	});
	refreshScroll();

	return {
		revert() {
			tween.scrollTrigger?.kill();
			tween.kill();
			instance.revert();
		},
	};
}
